import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ReturnQueue, ReturnRequestView } from '@miraki/contracts';
import { api, ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Empty, Field, Money, Note, PageHeader, Panel, Rail, Spinner, Table, Tabs } from '../ui';
import { StockDesk } from './StockDesk';
import { invalidateAll } from '../api/write-keys';
import {
  LINE_TONE, decidable, decisionPayload, draftCounts, receiptPayload, receivable, refusalLines, releasing, stage,
  type Draft, type Stage,
} from './returns-desk';

/**
 * A-43 Returns & Stock Verification — CN-13, CN-14, D71, DB-08. The work-queue archetype (A-02):
 * the queue on the left, undecided first; the request on the right.
 *
 * Two actions, each behind its own permission and each PER PIECE:
 *   - DECIDE (`return.decide`, D71): every requested piece gets its own Approve or Refuse, a
 *     refusal its own reason. Pieces left alone stay requested. No "approve all".
 *   - RECEIVE (`return.receive`, CN-14): tick the approved pieces that arrived. Each moves
 *     HELD → RETURNED, and that is the moment its value leaves budget in use. Shown as BUDGET
 *     released — there is no credit note and nothing was owed (CN-14).
 *
 * The second tab is the stock half (`StockDesk.tsx`): CN-09 recalls, CN-10 losses and CN-11 counts,
 * each with its open decision (D20, D39/D81, D40) made a portal setting rather than guessed. A
 * recall is a return request Miraki raises, so once raised it appears on THIS tab, born approved,
 * and is received like any other.
 */

const STAGE_TONE: Record<Stage, 'us' | 'run' | 'done'> = { DECIDE: 'us', GOODS: 'run', DONE: 'done' };
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

function Amounts({ list }: { list: ReadonlyArray<{ currency: string; amount: string }> }) {
  const { t } = useTranslation();
  if (list.length === 0) return <span className="mk-dim">{t('returnsDesk.none')}</span>;
  return <>{list.map((a) => <div key={a.currency}><Money amount={a.amount} currency={a.currency} /></div>)}</>;
}

export function ReturnsDesk() {
  const { t } = useTranslation();
  const { can } = useSession();
  const stock = can('stock.verify') || can('stock.loss_outcome');
  const requests = can('return.view');
  const [tab, setTab] = useState<'requests' | 'stock'>(requests ? 'requests' : 'stock');
  return (
    <>
      {stock && requests ? (
        <Tabs
          tabs={[{ id: 'requests', label: t('returnsDesk.tabRequests') }, { id: 'stock', label: t('returnsDesk.tabStock') }]}
          active={tab} onChange={(id) => setTab(id as 'requests' | 'stock')}
        />
      ) : null}
      {tab === 'stock' && stock ? <><PageHeader title={t('returnsDesk.title')} sub={t('returnsDesk.tabStock')} /><StockDesk /></> : <RequestsDesk />}
    </>
  );
}

function RequestsDesk() {
  const { t } = useTranslation();
  const { portal } = useSession();
  const [selected, setSelected] = useState<string | null>(null);
  // The portal is in the key: after a switch, the other portal's queue must not render (SC-02).
  const key = ['portal', portal?.code ?? null, 'returns'];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => api.get<ReturnQueue>('/returns') });

  if (isLoading) return <Spinner />;
  if (error || !data) return <Alert tone="stop">{error instanceof Error ? error.message : t('returnsDesk.failed')}</Alert>;

  const current = data.requests.find((r) => r.id === selected) ?? data.requests[0] ?? null;
  const waiting = data.requests.filter((r) => stage(r) === 'DECIDE').length;

  return (
    <>
      <PageHeader title={t('returnsDesk.title')} sub={t('returnsDesk.sub', { day: data.asOf })} />
      {data.requests.length === 0 ? (
        <Panel><Empty title={t('returnsDesk.emptyTitle')} body={t('returnsDesk.emptyBody')} /></Panel>
      ) : (
        <div className="mk-grid2">
          <Panel title={t('returnsDesk.queueTitle', { n: waiting })}>
            {data.requests.map((r) => {
              const s = stage(r);
              const on = r.id === current?.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  className="mk-qrow"
                  aria-selected={on}
                  onClick={() => setSelected(r.id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                    padding: 'var(--mk-space-3) var(--mk-space-4)', border: 0,
                    borderBottom: '1px solid var(--mk-rule)',
                    borderLeft: `2px solid ${on ? 'var(--mk-brand)' : 'transparent'}`,
                    background: on ? 'var(--mk-brand-wash)' : 'var(--mk-surface)',
                    font: 'inherit', color: 'inherit',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--mk-space-3)' }}>
                    <Chip tone={STAGE_TONE[s]}>{t(`returnsDesk.stage.${s}`)}</Chip>
                    {r.origin === 'MIRAKI' ? <Chip tone="them">{t('returnsDesk.recall')}</Chip> : null}
                    <b>{r.retailerName}</b>
                    <span className="mk-spacer" />
                    <span className="mk-hint">{day(r.requestedAt)}</span>
                  </div>
                  <div className="mk-hint">
                    {t('returnsDesk.pieces', { n: r.lines.length })} · {t('returnsDesk.countsLine', {
                      requested: r.counts.REQUESTED, approved: r.counts.APPROVED, received: r.counts.RECEIVED,
                    })}
                  </div>
                </button>
              );
            })}
          </Panel>
          {current ? <Request key={current.id} r={current} queryKey={key} /> : null}
        </div>
      )}
    </>
  );
}

function Request({ r, queryKey }: { r: ReturnRequestView; queryKey: unknown[] }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>({});
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [receivedOn, setReceivedOn] = useState('');
  const [failure, setFailure] = useState<string[] | null>(null);
  const [released, setReleased] = useState<ReadonlyArray<{ currency: string; amount: string }> | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const mayDecide = can('return.decide') && r.withdrawnAt === null;
  const mayReceive = can('return.receive');
  const jobOf = (id: string) => r.lines.find((l) => l.pieceId === id)?.jobNumber;
  const choose = (pieceId: string, choice: 'APPROVE' | 'REFUSE' | null) => setDraft((d) => {
    const next = { ...d };
    if (choice === null) delete next[pieceId];
    else next[pieceId] = { choice, reason: d[pieceId]?.reason ?? '' };
    return next;
  });

  async function send(path: string, body: unknown, after: (res: unknown) => void) {
    setFailure(null);
    setBusy(true);
    try {
      const res = await api.post(`/returns/${r.id}/${path}`, body);
      after(res);
      await invalidateAll(qc, [queryKey]);
    } catch (err) {
      const lines = refusalLines(err, jobOf);
      setFailure(lines ? lines.map((l) => t(l.key, l.values)) : [err instanceof ApiError ? err.message : t('returnsDesk.failed')]);
    } finally {
      setBusy(false);
    }
  }

  const decision = decisionPayload(r.lines, draft);
  const tally = draftCounts(r.lines, draft);
  const receipt = receiptPayload(r.lines, ticked, receivedOn);
  const willRelease = releasing(r.lines, ticked);

  return (
    <Panel title={r.retailerName} actions={<Chip tone={STAGE_TONE[stage(r)]}>{t(`returnsDesk.stage.${stage(r)}`)}</Chip>}>
      <div className="mk-pbody">
        <Rail items={[
          [t('returnsDesk.requested'), day(r.requestedAt)],
          [t('returnsDesk.lastDecided'), day(r.lastDecidedAt)],
          [t('returnsDesk.released'), <Amounts key="r" list={r.released} />],
          [t('returnsDesk.awaiting'), <Amounts key="a" list={r.awaiting} />],
        ]} />
        <p><b>{t(r.origin === 'MIRAKI' ? 'returnsDesk.recallRemarks' : 'returnsDesk.remarks')}</b> {r.remarks}</p>
        {r.origin === 'MIRAKI' ? <Note>{t('returnsDesk.recalledBy')}</Note> : null}
        {r.withdrawnAt ? <Alert tone="them">{t('returnsDesk.withdrawn', { day: day(r.withdrawnAt) })}</Alert> : null}
        <Note>{t('returnsDesk.budgetNotMoney')}</Note>
        {failure ? <Alert tone="stop"><ul>{failure.map((f) => <li key={f}>{f}</li>)}</ul></Alert> : null}
        {released ? <Alert tone="done">{t('returnsDesk.releasedNow')} <Amounts list={released} /></Alert> : null}

        <Table
          rows={[...r.lines]}
          rowKey={(l) => l.pieceId}
          columns={[
            { header: t('returnsDesk.colJob'), render: (l) => <><b>{l.jobNumber}</b><div className="mk-hint">{l.description}</div></> },
            { header: t('returnsDesk.colValue'), numeric: true, render: (l) => <Money amount={l.wholesaleValue} currency={l.currency} /> },
            { header: t('returnsDesk.colDaysHeld'), numeric: true, render: (l) => l.daysHeld ?? '—' },
            {
              header: t('returnsDesk.colState'),
              render: (l) => (
                <>
                  <Chip tone={LINE_TONE[l.state]}>{t(`returnsDesk.state.${l.state}`)}</Chip>
                  {l.refusalReason ? <div className="mk-hint">{l.refusalReason}</div> : null}
                  {l.receivedAt ? <div className="mk-hint">{t('returnsDesk.receivedOn', { day: day(l.receivedAt) })}</div> : null}
                  {l.note ? <div className="mk-hint">“{l.note}”</div> : null}
                </>
              ),
            },
            {
              header: '',
              render: (l) => {
                if (mayDecide && decidable(l)) {
                  const d = draft[l.pieceId];
                  return (
                    <div role="group" aria-label={t('returnsDesk.decideFor', { job: l.jobNumber })}>
                      {/*
                        * D71 — each radio and its own word are ONE unwrappable unit.
                        *
                        * This is the table's narrowest column and the control was plain inline
                        * markup, so the space between a radio and its own label text was a legal
                        * wrap point: at 1568px it broke after Approve's word, leaving Refuse's
                        * radio stranded at the end of one line and the word "Refuse" at the start
                        * of the next. On a control that records a decision PER PIECE that is not
                        * cosmetic — it is ambiguity about which radio means what. `.mk-choices`
                        * wraps BETWEEN the two choices and never inside one.
                        */}
                      <div className="mk-choices">
                        <label>
                          <input type="radio" name={`d-${l.pieceId}`} checked={d?.choice === 'APPROVE'} onChange={() => choose(l.pieceId, 'APPROVE')} />
                          <span>{t('returnsDesk.approve')}</span>
                        </label>
                        <label>
                          <input type="radio" name={`d-${l.pieceId}`} checked={d?.choice === 'REFUSE'} onChange={() => choose(l.pieceId, 'REFUSE')} />
                          <span>{t('returnsDesk.refuse')}</span>
                        </label>
                        {d ? <Button size="sm" tone="ghost" onClick={() => choose(l.pieceId, null)}>{t('returnsDesk.leave')}</Button> : null}
                      </div>
                      {d?.choice === 'REFUSE' ? (
                        <Field label={t('returnsDesk.refusalReason', { job: l.jobNumber })}>
                          <input value={d.reason} onChange={(e) => setDraft({ ...draft, [l.pieceId]: { choice: 'REFUSE', reason: e.target.value } })} />
                        </Field>
                      ) : null}
                    </div>
                  );
                }
                if (mayReceive && receivable(l)) {
                  return (
                    // Same column, same hazard as the decision radios above: the tick and its word stay together.
                    <div className="mk-choices">
                      <label>
                        <input
                          type="checkbox" checked={ticked.has(l.pieceId)}
                          aria-label={t('returnsDesk.arrived', { job: l.jobNumber })}
                          onChange={(e) => {
                            const next = new Set(ticked);
                            if (e.target.checked) next.add(l.pieceId); else next.delete(l.pieceId);
                            setTicked(next);
                          }}
                        />
                        <span>{t('returnsDesk.arrivedShort')}</span>
                      </label>
                    </div>
                  );
                }
                return null;
              },
            },
          ]}
        />

        {mayDecide && r.lines.some(decidable) ? (
          <div style={{ marginTop: 'var(--mk-space-4)' }}>
            {!decision.ok && decision.reason === 'REASON_MISSING'
              ? <p className="mk-hint">{t('returnsDesk.reasonMissing', { jobs: decision.pieceIds.map((id) => jobOf(id) ?? id).join(', ') })}</p> : null}
            <Button
              disabled={!decision.ok || busy}
              onClick={() => decision.ok && void send('decision', decision.payload, () => setDraft({}))}
            >
              {t('returnsDesk.recordDecision', { approve: tally.approve, refuse: tally.refuse })}
            </Button>
            <span className="mk-hint"> {t('returnsDesk.decisionHint')}</span>
          </div>
        ) : null}

        {mayReceive && r.lines.some(receivable) ? (
          <div style={{ marginTop: 'var(--mk-space-4)', display: 'flex', gap: 'var(--mk-space-3)', alignItems: 'end', flexWrap: 'wrap' }}>
            <Field label={t('returnsDesk.receivedOnLabel')} hint={t('returnsDesk.receivedOnHint')}>
              <input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
            </Field>
            <Button
              disabled={!receipt || busy}
              onClick={() => receipt && void send('receipt', receipt, (res) => {
                setReleased((res as { releasedNow: Array<{ currency: string; amount: string }> }).releasedNow);
                setTicked(new Set());
              })}
            >
              {t('returnsDesk.recordReceipt', { n: receipt?.pieceIds.length ?? 0 })}
            </Button>
            {willRelease.length > 0 ? <span className="mk-hint">{t('returnsDesk.willRelease')} <Amounts list={willRelease} /></span> : null}
          </div>
        ) : null}

        {r.origin === 'MIRAKI' && can('return.recall') && r.lines.some(receivable) ? (
          <div style={{ marginTop: 'var(--mk-space-4)', display: 'flex', gap: 'var(--mk-space-3)', alignItems: 'end', flexWrap: 'wrap' }}>
            <Field label={t('returnsDesk.cancelReason')} hint={t('returnsDesk.cancelHint')}>
              <input value={cancelReason} maxLength={500} onChange={(e) => setCancelReason(e.target.value)} />
            </Field>
            <Button
              tone="ghost" disabled={busy || cancelReason.trim().length < 3}
              onClick={() => void (async () => {
                setFailure(null); setBusy(true);
                try {
                  await api.post(`/returns/recalls/${r.id}/cancel`, { pieceIds: r.lines.filter(receivable).map((l) => l.pieceId), reason: cancelReason.trim() });
                  setCancelReason('');
                  await invalidateAll(qc, [queryKey]);
                } catch (err) {
                  const lines = refusalLines(err, jobOf);
                  setFailure(lines ? lines.map((l) => t(l.key, l.values)) : [err instanceof ApiError ? err.message : t('returnsDesk.failed')]);
                } finally { setBusy(false); }
              })()}
            >
              {t('returnsDesk.cancelRecall', { n: r.lines.filter(receivable).length })}
            </Button>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
